import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import axios from "src/utils/baseAxios";
import AttachmentBlock from "src/components/TradePartner/AttachmentsBlock";
import CompensationExchange from "src/views/TradePartnerEU/CompensationExchange";
import { getUser } from "src/utils/Common";
import { connect } from "react-redux";
import { getTasksRequest } from "../../store/actions/taskActions";
import { useTranslation } from "react-i18next"; // 🌍 i18n importu

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    padding: theme.spacing(5),
    fontFamily: "'Poppins','Inter',sans-serif",
    background: "linear-gradient(180deg, #f8fbff 0%, #f1f5ff 100%)",
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(2.5) },
  },
  backButton: {
    background: "linear-gradient(90deg, #739fd1, #4a86ca)",
    color: "#fff",
    fontWeight: 600,
    borderRadius: 12,
    padding: theme.spacing(1, 3),
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontSize: "0.85rem",
    boxShadow: "0 4px 12px rgba(99,132,241,0.3)",
    "&:hover": {
      background: "linear-gradient(90deg, #6190c7, #336eb1)",
      boxShadow: "0 6px 14px rgba(99,132,241,0.4)",
      transform: "translateY(-1px)",
    },
    marginBottom: theme.spacing(3),
  },
  sectionCard: {
    borderRadius: 20,
    background: "#fff",
    boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1b84ff",
    marginBottom: theme.spacing(2),
    display: "inline-block",
    position: "relative",
    "&::after": {
      content: '""',
      display: "block",
      width: 48,
      height: 3,
      background: "linear-gradient(90deg, #00B2FF, #0079FF)",
      borderRadius: 2,
      marginTop: 4,
    },
  },
  infoBox: {
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: theme.spacing(2.5),
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  woId: {
    fontWeight: 700,
    color: "#0079FF",
    fontSize: "1.2rem",
    marginBottom: theme.spacing(2.5),
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderRadius: 12,
    background: "linear-gradient(90deg, #f9fbff 0%, #eef3ff 100%)",
    boxShadow: "inset 0 0 4px rgba(0,0,0,0.05)",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "linear-gradient(90deg, #e8f0ff 0%, #dbe8ff 100%)",
      transform: "translateY(-2px)",
    },
  },
  icon: {
    fontSize: 20,
    marginRight: 12,
    color: "#0079FF",
    flexShrink: 0,
  },
  label: {
    fontWeight: 600,
    color: "#334155",
    fontSize: "0.85rem",
    marginBottom: 2,
  },
  value: {
    color: "#111827",
    fontSize: "0.95rem",
    fontWeight: 500,
  },
}));

function CompensationDetail({ workOrder, onBack, getTasksRequest, tasks }) {
  const classes = useStyles();
  const { t } = useTranslation("overview"); // 🌍 i18n hook
  const SessionUser = getUser();
  const [tradeInfo, setTradeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachmentForm, setAttachmentForm] = useState({ files: [] });
  const [attachmentsLoading, setAttachmentsLoading] = useState(true);

  useEffect(() => {
    if (!workOrder?.WorkOrderId) return;
    setLoading(true);
    setAttachmentsLoading(true);
    setTradeInfo(null);

    // // getTasksRequest(String(workOrder.WorkOrderId));
    fetchTradePartnerInfo(workOrder.ServiceShopId || SessionUser.ServiceShopId);
  }, [
    workOrder?.WorkOrderId,
    workOrder?.ServiceShopId,
    SessionUser.ServiceShopId,
  ]);

  const fetchTradePartnerInfo = async (serviceShopId) => {
    try {
      const res = await axios.get("/tradepartner/get-tradepartner-info", {
        params: { serviceShopId },
      });
      setTradeInfo(res.data?.Data || res.data?.data || null);
    } catch (err) {
      console.error("TradePartner info fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={classes.root}>
      {/* 🔙 Back Button */}
      <Button
        className={classes.backButton}
        onClick={onBack}
        startIcon={<ArrowBackIcon />}
      >
        {t("backToList")}
      </Button>

      {/* 1️⃣ TRADE PARTNER INFORMATION */}
      <Card className={classes.sectionCard}>
        <Typography className={classes.sectionLabel}>
          💼 {t("tradePartnerInformation")}
        </Typography>

        <Box className={classes.infoBox}>
          <Typography className={classes.woId}>
            #{workOrder.WorkOrderId} - {tradeInfo?.ServiceShop || "—"}
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : tradeInfo ? (
            <Grid container spacing={3}>
              {/* 👤 Contact Person */}
              <Grid item xs={12} sm={6} md={6}>
                <Box className={classes.infoItem}>
                  <span className={classes.icon}>👤</span>
                  <Box>
                    <Typography className={classes.label}>
                      {t("contactPerson")}
                    </Typography>
                    <Typography className={classes.value}>
                      {tradeInfo.FirstName} {tradeInfo.LastName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* 📞 Phone */}
              <Grid item xs={12} sm={6} md={6}>
                <Box className={classes.infoItem}>
                  <span className={classes.icon}>📞</span>
                  <Box>
                    <Typography className={classes.label}>{t("phone")}</Typography>
                    <Typography className={classes.value}>
                      {tradeInfo.FullPhone || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* 📍 City */}
              <Grid item xs={12} sm={6} md={6}>
                <Box className={classes.infoItem}>
                  <span className={classes.icon}>📍</span>
                  <Box>
                    <Typography className={classes.label}>{t("city")}</Typography>
                    <Typography className={classes.value}>
                      {tradeInfo.City || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* 🏠 Address */}
              <Grid item xs={12} sm={6} md={6}>
                <Box className={classes.infoItem}>
                  <span className={classes.icon}>🏠</span>
                  <Box>
                    <Typography className={classes.label}>
                      {t("address")}
                    </Typography>
                    <Typography className={classes.value}>
                      {tradeInfo.Address || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography color="error">{t("tradePartnerInfoNotFound")}</Typography>
          )}
        </Box>
      </Card>

      {/* 2️⃣ ATTACHMENTS */}
      <Card className={classes.sectionCard}>
        <AttachmentBlock
          form={attachmentForm}
          setForm={setAttachmentForm}
          loading={attachmentsLoading}
          workOrderId={workOrder.WorkOrderId}
          serviceShopId={workOrder.ServiceShopId}
          tenantId={workOrder.TenantId}
          readOnly={false}
        />
      </Card>

      {/* 3️⃣ EXCHANGE PROCESS */}
      <Card className={classes.sectionCard}>
        <Typography className={classes.sectionLabel}>
          🔄 {t("exchangeProcess")}
        </Typography>
        <CompensationExchange
          ReceiptNo={String(workOrder.WorkOrderId)}
          readonly={false}
          tasks={tasks}
          setTask={() => {}}
          attachmentForm={attachmentForm}
          setAttachmentForm={setAttachmentForm}
          setAttachmentsLoading={setAttachmentsLoading}
        />
      </Card>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  tasks: state.tasks1?.tasks || [],
});

export default connect(mapStateToProps, { getTasksRequest })(CompensationDetail);
